const ASSETS = 'https://prospecting.semdeck.com/mockup-assets/landscaping';

const DEFAULT_COPY = {
  eyebrow: 'Local Property Care',
  headline: 'Your Yard Deserves the Best Care Year-Round',
  tagline: 'From summer lawns to cleaned-up outdoor spaces, we keep your property looking sharp in every season.',
  about: 'COMPANY_NAME helps local homeowners keep their properties clean, healthy, and ready for every season. The work is practical, straightforward, and focused on the details people notice first.',
  services: [
    { name: 'Lawn Care', desc: 'Regular mowing, edging, fertilization, and weed control for a cleaner, healthier lawn.' },
    { name: 'Landscape Cleanup', desc: 'Bed cleanup, mulch, pruning, and seasonal refreshes that make the whole property feel cared for.' },
    { name: 'Hardscaping', desc: 'Patios, walkways, retaining walls, and outdoor spaces built around how you use the yard.' },
  ],
};

const SERVICE_ICONS = [
  '<path d="M12 22c1-3 6-6 6-11a6 6 0 0 0-12 0c0 5 5 8 6 11zm0-14a2 2 0 1 1 0 4 2 2 0 0 1 0-4z"/>',
  '<path d="M2 22h20v-2H2v2zm2-4h4v-8H4v8zm6 0h4V6h-4v12zm6 0h4v-5h-4v5z"/>',
  '<path d="M12 2c-5.33 4.55-8 8.48-8 11.8 0 4.98 3.8 8.2 8 8.2s8-3.22 8-8.2C20 10.48 17.33 6.55 12 2zm0 18c-3.35 0-6-2.57-6-6.2 0-2.34 1.95-5.44 6-9.14 4.05 3.7 6 6.79 6 9.14 0 3.63-2.65 6.2-6 6.2z"/>',
];

const DEFAULT_HERO_USPS = [
  'On time, ready to work',
  'Clear pricing before work begins',
  'Clean, professional crews',
  'Locally owned and responsive',
];

function escapeHtml(value) {
  return String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function phoneDigits(value) {
  return String(value || '').replace(/\D/g, '');
}

function clampServices(copy) {
  return Array.isArray(copy.services) && copy.services.length >= 3
    ? copy.services.slice(0, 3)
    : DEFAULT_COPY.services;
}

function buildServiceItems(services) {
  return services.map((service, index) => `
                    <li class="cs-item">
                        <div class="cs-icon-wrapper" aria-hidden="true">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">${SERVICE_ICONS[index] || SERVICE_ICONS[0]}</svg>
                        </div>
                        <h3 class="cs-h3">${escapeHtml(service.name || DEFAULT_COPY.services[index]?.name || 'Property Care')}</h3>
                        <p class="cs-item-text">${escapeHtml(service.desc || DEFAULT_COPY.services[index]?.desc || 'Reliable service for a cleaner, easier-to-maintain property.')}</p>
                        <a href="#cta-497" class="cs-link">${escapeHtml(service.name || 'Service')} Services</a>
                    </li>`).join('');
}

function buildHeroUsps(items) {
  const usps = Array.isArray(items) && items.length ? items.slice(0, 4) : DEFAULT_HERO_USPS;

  return usps.map((item) => `<li>${escapeHtml(item)}</li>`).join('');
}

function starSvgs() {
  return Array.from({ length: 5 }).map(() =>
    '<svg class="cs-star" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>'
  ).join('');
}

function buildBrandMark(company, logoUrl) {
  if (logoUrl) {
    return `<img class="cs-logo-img" src="${escapeHtml(logoUrl)}" alt="${company} logo" width="168" height="48" decoding="async">`;
  }

  return `<span class="mockup-wordmark" aria-label="${company}">
            <span class="mockup-wordmark-icon" aria-hidden="true"></span>
            <span class="mockup-wordmark-text">${company}</span>
          </span>`;
}

export function generateLandscapingMockup(templateId, data = {}) {
  if (templateId !== 10) return '<p>Unknown landscaping template ID</p>';

  const copy = data.copy || {};
  const company = escapeHtml(data.company || 'Your Landscaping Company');
  const phone = escapeHtml(data.phone || '(440) 555-7890');
  const email = escapeHtml(data.email || 'info@example.com');
  const address = escapeHtml(data.address || '');
  const digits = phoneDigits(phone);
  const services = clampServices(copy);
  const eyebrow = escapeHtml(copy.eyebrow || DEFAULT_COPY.eyebrow);
  const headline = escapeHtml(copy.headline || DEFAULT_COPY.headline).replace(/\n/g, '<br>');
  const tagline = escapeHtml(copy.tagline || DEFAULT_COPY.tagline);
  const about = escapeHtml((copy.about || DEFAULT_COPY.about).replace('COMPANY_NAME', company));
  const logoUrl = data.logoUrl && String(data.logoUrl).trim() ? String(data.logoUrl).trim() : '';
  const brandMark = buildBrandMark(company, logoUrl);
  const serviceItems = buildServiceItems(services);
  const heroUsps = buildHeroUsps(copy.usps || copy.heroUsps);
  const stars = starSvgs();

  return `<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="robots" content="noindex, nofollow">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <meta name="description" content="${company} website mockup by SEMdeck.">
    <title>Home | ${company}</title>
    <link rel="icon" type="image/svg+xml" href="${ASSETS}/logo.svg">
    <link rel="stylesheet" href="${ASSETS}/root.css">
    <link rel="stylesheet" href="${ASSETS}/critical.css">
    <link rel="stylesheet" href="${ASSETS}/local.css">
    <style>
      #seasonal-1440 { background-image: url('${ASSETS}/landscaping-seasonal.jpg'); }
      #cs-navigation .cs-logo-img { max-width: 13rem; object-fit: contain; }
      #cs-footer-841 .cs-logo-img { max-width: 12rem; object-fit: contain; }
      .mockup-wordmark { display: inline-flex; align-items: center; gap: 0.75rem; color: var(--headerColor); font-weight: 900; line-height: 1.05; max-width: 17rem; }
      .mockup-wordmark-icon { width: 2.75rem; height: 2.75rem; border-radius: 50%; background: linear-gradient(135deg, var(--primary) 0 48%, var(--primaryShade) 48% 100%); position: relative; flex: none; box-shadow: inset 0 0 0 0.25rem rgba(255,255,255,0.75); }
      .mockup-wordmark-icon:before { content: ""; width: 1.55rem; height: 0.82rem; border-radius: 100% 0 100% 0; background: #fff; position: absolute; left: 0.63rem; top: 0.9rem; transform: rotate(-28deg); }
      .mockup-wordmark-text { display: block; font-size: clamp(1rem, 1.4vw, 1.18rem); letter-spacing: 0; }
      #cs-footer-841 .mockup-wordmark { color: #fff; }
    </style>
</head>
<body>
    <a class="skip" href="#main">Click To Skip To Main Content</a>
    <header id="cs-navigation" aria-label="site navigation">
        <div class="cs-top-bar">
            <div class="cs-top-container">
                <a href="tel:${digits}" class="cs-top-link">${phone}</a>
                <a href="mailto:${email}" class="cs-top-link">${email}</a>
            </div>
        </div>
        <div class="cs-container">
            <a href="#" class="cs-logo" aria-label="Back to home">${brandMark}</a>
            <button class="cs-toggle" aria-label="mobile menu toggle" aria-expanded="false"><span class="cs-line"></span><span class="cs-line"></span><span class="cs-line"></span></button>
            <div class="cs-ul-wrapper">
                <nav><ul class="cs-ul"><li class="cs-li"><a href="#" class="cs-li-link cs-active">Home</a></li><li class="cs-li"><a href="#about-1892" class="cs-li-link">About</a></li><li class="cs-li"><a href="#services-1283" class="cs-li-link">Services</a></li><li class="cs-li"><a href="#cta-497" class="cs-li-link">Contact</a></li></ul></nav>
                <a href="#cta-497" class="cs-button-solid">Free Estimate</a>
            </div>
        </div>
    </header>

    <main id="main">
        <section id="hero-1720">
            <div class="cs-container">
                <div class="cs-content">
                    <span class="cs-topper">${eyebrow}</span>
                    <h1 class="cs-title">${headline}</h1>
                    <p class="cs-text">${tagline}</p>
                    <ul class="cs-hero-list">${heroUsps}</ul>
                    <a href="#cta-497" class="cs-button-solid">Get Your Free Estimate</a>
                </div>
                <div class="cs-hero-collage" aria-label="Landscaping project highlights">
                    <picture class="cs-hero-photo cs-hero-main"><img src="${ASSETS}/landscaping-hero.jpg" alt="Freshly landscaped property with green lawn" width="630" height="630" loading="eager" decoding="async"></picture>
                    <picture class="cs-hero-photo cs-hero-small"><img src="${ASSETS}/landscaping-collage-detail.jpg" alt="Finished garden bed and walkway" width="430" height="330" loading="eager" decoding="async"></picture>
                    <div class="cs-hero-usp"><strong>5★</strong><span>Google Rated<br>Local Service</span></div>
                </div>
            </div>
        </section>

        <section id="services-1283">
            <div class="cs-container">
                <div class="cs-content">
                    <span class="cs-topper">What We Do</span>
                    <h2 class="cs-title">Services Built Around Your Property</h2>
                    <p class="cs-text">Whether you need weekly maintenance or a complete outdoor transformation, the crew brings the same attention to detail on every project.</p>
                </div>
                <ul class="cs-card-group">${serviceItems}</ul>
            </div>
        </section>

        <section id="about-1892">
            <div class="cs-container">
                <div class="cs-image-group"><div class="cs-picture-large"><img src="${ASSETS}/landscaping-about.jpg" alt="Landscaping crew installing a stone patio" width="570" height="400" loading="lazy" decoding="async"></div></div>
                <div class="cs-content">
                    <span class="cs-topper">Who We Are</span>
                    <h2 class="cs-title">Rooted Locally, Growing With The Community</h2>
                    <p class="cs-text">${about}</p>
                    <p class="cs-text">The best small-business sites make the next step obvious, show real services clearly, and make the company feel trustworthy before anyone picks up the phone.</p>
                    <a href="#cta-497" class="cs-button-solid">More About Our Team</a>
                </div>
            </div>
        </section>

        <section id="stats-799" class="cs-dark-section">
            <div class="cs-container">
                <div class="cs-stat"><span class="cs-number">5★</span><span class="cs-desc">Google Rated</span></div>
                <div class="cs-stat"><span class="cs-number">3</span><span class="cs-desc">Core Services</span></div>
                <div class="cs-stat"><span class="cs-number">24hr</span><span class="cs-desc">Estimate Follow-Up</span></div>
                <div class="cs-stat"><span class="cs-number">100%</span><span class="cs-desc">Locally Operated</span></div>
            </div>
        </section>

        <section id="process-1188">
            <div class="cs-container">
                <div class="cs-content"><span class="cs-topper">How It Works</span><h2 class="cs-title">Simple Service, Clear Communication</h2><p class="cs-text">The process stays straightforward: talk through the property, build a clear plan, and make the work easy to approve.</p></div>
                <ol class="cs-card-group"><li class="cs-item"><span class="cs-step">01</span><h3 class="cs-h3">Walk The Property</h3><p class="cs-item-text">Look at the lawn, beds, access points, and seasonal needs so the estimate fits the real property.</p></li><li class="cs-item"><span class="cs-step">02</span><h3 class="cs-h3">Build The Plan</h3><p class="cs-item-text">Give the homeowner a practical scope, clear timing, and a direct recommendation for what should happen first.</p></li><li class="cs-item"><span class="cs-step">03</span><h3 class="cs-h3">Keep It Looking Sharp</h3><p class="cs-item-text">Handle the work, clean up well, and keep communication simple from the first visit through the final pass.</p></li></ol>
            </div>
        </section>

        <section id="visual-2031" class="cs-dark-section">
            <div class="cs-container">
                <div class="cs-content"><span class="cs-topper">Built For Curb Appeal</span><h2 class="cs-title">The Details People Notice From The Street</h2><p class="cs-text">Clean edges, healthy planting beds, bright walkways, and seasonal cleanup all work together.</p><a href="#services-1283" class="cs-button-solid">See Our Services</a></div>
                <div class="cs-collage" aria-label="Landscaping project image collage"><picture class="cs-picture cs-picture-main"><img src="${ASSETS}/landscaping-collage-main.jpg" alt="Landscaping project" width="640" height="520" loading="lazy" decoding="async"></picture><picture class="cs-picture cs-picture-small"><img src="${ASSETS}/landscaping-collage-detail.jpg" alt="Finished garden bed" width="430" height="330" loading="lazy" decoding="async"></picture><div class="cs-rating"><span class="cs-rating-number">5★</span><span class="cs-rating-text">Google Rated</span></div></div>
            </div>
        </section>

        <section id="seasonal-1440">
            <div class="cs-overlay"></div>
            <div class="cs-container">
                <div class="cs-content"><span class="cs-topper">Year-Round Help</span><h2 class="cs-title">One Crew For Growth, Maintenance, Cleanup, And Seasonal Service</h2><p class="cs-text">Properties change fast from season to season. This layout shows how one company can keep the message clear without overcomplicating the page.</p></div>
                <div class="cs-season-grid"><div class="cs-season"><h3 class="cs-h3">Spring</h3><p class="cs-item-text">Bed cleanup, fresh edges, mulch, and early lawn care.</p></div><div class="cs-season"><h3 class="cs-h3">Summer</h3><p class="cs-item-text">Routine mowing, trimming, and upkeep.</p></div><div class="cs-season"><h3 class="cs-h3">Fall</h3><p class="cs-item-text">Leaf cleanup, pruning, and cold-weather prep.</p></div><div class="cs-season"><h3 class="cs-h3">Winter</h3><p class="cs-item-text">Snow and seasonal property support where relevant.</p></div></div>
            </div>
        </section>

        <section id="reviews-727">
            <div class="cs-container">
                <div class="cs-content"><span class="cs-topper">Testimonials</span><h2 class="cs-title">What Customers Could Say</h2></div>
                <ul class="cs-card-group"><li class="cs-item"><div class="cs-stars" aria-label="5 out of 5 stars">${stars}</div><p class="cs-review">Lorem ipsum dolor sit amet, consectetur adipiscing elit. Integer posuere erat a ante venenatis dapibus posuere velit aliquet.</p><div class="cs-flex-group"><span class="cs-name">Customer Name</span><span class="cs-job">Residential Customer</span></div></li><li class="cs-item"><div class="cs-stars" aria-label="5 out of 5 stars">${stars}</div><p class="cs-review">Vestibulum id ligula porta felis euismod semper. Donec ullamcorper nulla non metus auctor fringilla.</p><div class="cs-flex-group"><span class="cs-name">Customer Name</span><span class="cs-job">Property Owner</span></div></li><li class="cs-item"><div class="cs-stars" aria-label="5 out of 5 stars">${stars}</div><p class="cs-review">Cras mattis consectetur purus sit amet fermentum. Maecenas faucibus mollis interdum.</p><div class="cs-flex-group"><span class="cs-name">Customer Name</span><span class="cs-job">Local Customer</span></div></li></ul>
            </div>
        </section>

        <section id="cta-497">
            <div class="cs-container"><span class="cs-topper">Get Started</span><h2 class="cs-title">Ready to Transform Your Property?</h2><p class="cs-text">Call or send a message. The next step should be obvious, fast, and low-pressure.</p><a href="tel:${digits}" class="cs-button-solid">Call ${phone}</a></div>
        </section>

    </main>

    <footer id="cs-footer-841">
        <div class="cs-container">
            <div class="cs-logo-group"><a href="#" class="cs-logo" aria-label="Back to home">${brandMark}</a><p class="cs-text">Professional property care with clear services, easy contact options, and a cleaner online presence.</p></div>
            <div class="cs-nav-group"><span class="cs-header">Quick Links</span><ul class="cs-nav-list"><li><a href="#" class="cs-nav-link">Home</a></li><li><a href="#about-1892" class="cs-nav-link">About</a></li><li><a href="#services-1283" class="cs-nav-link">Services</a></li><li><a href="#cta-497" class="cs-nav-link">Contact</a></li></ul></div>
            <div class="cs-contact-group"><span class="cs-header">Contact</span><ul class="cs-contact-list"><li><a href="tel:${digits}" class="cs-contact-link">${phone}</a></li><li><a href="mailto:${email}" class="cs-contact-link">${email}</a></li></ul></div>
        </div>
        <div class="cs-bottom"><span class="cs-copyright">&copy; 2026 ${company}. All Rights Reserved.</span><span class="cs-credit">Designed and Developed by <a href="https://semdeck.com" target="_blank" rel="noopener">SEMdeck</a></span></div>
    </footer>
    <script>
      const nav = document.getElementById('cs-navigation');
      const toggle = document.querySelector('#cs-navigation .cs-toggle');
      if (toggle) toggle.addEventListener('click', () => { const open = nav.classList.toggle('cs-active'); toggle.setAttribute('aria-expanded', open); });
      window.addEventListener('scroll', () => document.body.classList.toggle('scroll', window.scrollY > 50));
      const scrollTopButton = document.createElement('button');
      scrollTopButton.className = 'cs-scroll-top';
      scrollTopButton.type = 'button';
      scrollTopButton.setAttribute('aria-label', 'Scroll to top');
      scrollTopButton.innerHTML = '<span aria-hidden="true">↑</span>';
      document.body.appendChild(scrollTopButton);
      const toggleScrollTopButton = () => scrollTopButton.classList.toggle('cs-visible', window.scrollY > 500);
      scrollTopButton.addEventListener('click', () => window.scrollTo({ top: 0, behavior: 'smooth' }));
      window.addEventListener('scroll', toggleScrollTopButton);
      toggleScrollTopButton();
    </script>
</body>
</html>`;
}
